import { useEffect, useState } from "react";
import { Cohort } from "../../model/cohort";
import { Stream, StudentGroup } from "../../model/stream";
import { getStudentGroups } from "../../service/student-group-service";
import { BaseList } from "../../../../component/list/base-list";
import { columns } from "./cohort-stream-group-column-definition";
import commonStyles from "../../../style/common.module.css";
import { Button, Typography } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CohortStreamGroupHandler from "./cohort-stream-group-handler";
import CohortStreamGroupMembers from "./cohort-stream-group-members";

export default function CohortStreamGroups({
    cohort,
    stream,
    onBack,
}: {
    cohort: Cohort,
    stream: Stream,
    onBack: () => void,
}) {
    const [loading, setLoading] = useState(true);
    const [groups, setGroups] = useState<StudentGroup[]>([]);
    const [selectedGroup, setSelectedGroup] = useState<StudentGroup | undefined>();
    const [showForm, setShowForm] = useState(false);
    const [showMembers, setShowMembers] = useState(false);

    useEffect(() => {
        loadGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stream.id]);

    const loadGroups = () => {
        setLoading(true);
        getStudentGroups(stream.id!).then(result => {
            setGroups(result ?? []);
            setLoading(false);
        });
    }

    const openAdd = () => {
        setSelectedGroup(undefined);
        setShowForm(true);
        setShowMembers(false);
    }

    const openEdit = (groupId: string) => {
        const group = groups.find(_ => _.id === Number(groupId));
        setSelectedGroup(group);
        setShowForm(true);
        setShowMembers(false);
    }

    const openMembers = (group: StudentGroup) => {
        setSelectedGroup(group);
        setShowMembers(true);
        setShowForm(false);
    }

    const handleSaved = () => {
        setShowForm(false);
        loadGroups();
    }

    const handleDeleted = () => {
        setShowForm(false);
        setSelectedGroup(undefined);
        loadGroups();
    }

    const handleBackFromForm = () => {
        setShowForm(false);
        setShowMembers(false);
        setSelectedGroup(undefined);
    }

    if (showForm) {
        return (
            <CohortStreamGroupHandler
                stream={stream}
                group={selectedGroup}
                onSaved={handleSaved}
                onDeleted={handleDeleted}
                onCancel={handleBackFromForm}
            />
        );
    }

    if (showMembers && selectedGroup) {
        return (
            <CohortStreamGroupMembers
                cohort={cohort}
                stream={stream}
                group={selectedGroup}
                onBack={handleBackFromForm}
            />
        );
    }

    return (
        <div className={commonStyles.content}>
            <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 1 }}>
                Back to Streams
            </Button>
            <Typography variant="h6" sx={{ mb: 1 }}>
                Groups in: {stream.name}
            </Typography>
            <BaseList
                storageKey={`STREAM_${stream.id}_GROUPS`}
                rows={groups}
                columns={columns(openMembers)}
                loading={loading}
                localeText={{ noRowsLabel: "No groups" }}
                getRowId={(row) => row?.id}
                onRowClick={(e) => openEdit(e.row?.id)}
                onAddClick={() => openAdd()}
                addButtonText="Add group"
            />
        </div>
    );
}
